/**
 * `DateUtil` is a utility object that provides methods for transforming dates.
 *
 * @category DataManipulation
 */
export const DateUtil: DateTransformer = {
  toDateWithTime,
  toCurrentTime,
  toShortDate,
  toWeekDayMonthDayYear,
  toWeekDayMonthDay,
  toMonthYear,
  isTodayOrFutureDate,
};

/**
 * Converts a date string to the format `DD/MM/YYYY HH:mm` based on the user's locale.
 *
 * @param dateString - The date string to be formatted.
 * @returns A formatted date string in `DD/MM/YYYY HH:mm` format.
 *
 * @example
 * ```ts
 * DateUtil.toDateWithTime('2024-11-12T14:27:00.000Z');
 * ```
 */
function toDateWithTime(dateString: string): string {
  const date = new Date(dateString);
  const formattedDate = new Intl.DateTimeFormat(navigator.language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

  return formattedDate;
}

/**
 * Returns the current time in the format `hh:mm (UTC)` based on the user's locale.
 *
 * @returns The current time in the `hh:mm (UTC)` format.
 * @example
 * ```ts
 * DateUtil.toCurrentTime();
 * ```
 */
function toCurrentTime(): string {
  return new Intl.DateTimeFormat(navigator.language, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date());
}

/**
 * Converts a date string to the format `MMM DD, YYYY` based on the user's locale.
 *
 * @param dateString - The date string to be formatted.
 * @returns A formatted date string in `MMM DD, YYYY` format.
 * @example
 * ```ts
 * DateUtil.toShortDate('2024-11-12T14:27:00.000Z');
 * ```
 */
function toShortDate(dateString: string): string {
  const date = new Date(dateString);
  const formattedDate = new Intl.DateTimeFormat(navigator.language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);

  return formattedDate;
}

/**
 * Converts a date string to the format `EEE, MMM DD, YYYY` based on the user's locale.
 *
 * @param dateString - The date string to be formatted.
 * @returns A formatted date string in `EEE, MMM DD, YYYY` format.
 *
 * @example
 * ```ts
 * DateUtil.toWeekDayMonthDayYear('2024-11-12T14:27:00.000Z');
 * ```
 */
function toWeekDayMonthDayYear(dateString: string): string {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return '';
  }
  const formattedDate = new Intl.DateTimeFormat(navigator.language, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);

  return formattedDate;
}

/**
 * Converts a date string to the format `EEE, MMM DD` based on the user's locale.
 *
 * @param dateString - The date string to be formatted.
 * @returns A formatted date string in `EEE, MMM DD` format.
 *
 * @example
 * ```ts
 * DateUtil.toWeekDayMonthDay('2024-11-12T14:27:00.000Z');
 * ```
 */
function toWeekDayMonthDay(dateString: string): string {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return '';
  }
  const formattedDate = new Intl.DateTimeFormat(navigator.language, {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
  }).format(date);

  return formattedDate;
}

/**
 * Converts a date string to the format `MMM YYYY` based on the user's locale.
 *
 * @param dateString - The date string to be formatted.
 * @returns A formatted date string in `MMM YYYY` format.
 * @example
 * ```ts
 * DateUtil.toMonthYear('2021-10-22');
 * ```
 */
function toMonthYear(dateString: string): string {
  const date = new Date(dateString);
  const formattedDate = new Intl.DateTimeFormat(navigator.language, {
    month: 'short',
    year: 'numeric',
  }).format(date);

  return formattedDate;
}

/**
 * Checks if a given date string represents today's date or a future date.
 *
 * @param dateStr - The date string to be checked.
 * @returns `true` if the date is today or in the future, `false` otherwise.
 * @example
 * ```ts
 * DateUtil.isTodayOrFutureDate('2025-03-11'); // Returns true if today's date is 2025-03-11 or earlier
 * ```
 */
function isTodayOrFutureDate(dateStr: string): boolean {
  return (
    new Date(dateStr).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
  );
}
/**
 * `DateTransformer` defines methods for transforming dates.
 */
export type DateTransformer = {
  /**
   * Converts a date string to the format `DD/MM/YYYY HH:mm` based on the user's locale.
   *
   * @param dateString - The date string to be formatted.
   * @returns A formatted date string in `DD/MM/YYYY HH:mm` format.
   * @example
   * ```ts
   * import { DateUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
   * DateUtil.toDateWithTime('2024-11-12T14:27:00.000Z'); // Returns "12/11/2024 14:27"
   * ```
   */
  toDateWithTime: (dateString: string) => string;

  /**
   * Returns the current time in the format `hh:mm (UTC)` based on the user's locale.
   *
   * @returns The current time in the `hh:mm (UTC)` format.
   * @example
   * ```ts
   * import { DateUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
   * DateUtil.toCurrentTime(); // Returns "14:27 (UTC)"
   * ```
   */
  toCurrentTime: () => string;

  /**
   * Converts a date string to the format `MMM DD, YYYY` based on the user's locale.
   *
   * @param dateString - The date string to be formatted.
   * @returns A formatted date string in `MMM DD, YYYY` format.
   * @example
   * ```ts
   * import { DateUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
   * DateUtil.toShortDate('2024-11-12T14:27:00.000Z'); // Returns "Nov 12, 2024"
   * ```
   */
  toShortDate: (dateString: string) => string;

  /**
   * Converts a date string to the format `EEE, MMM DD, YYYY` based on the user's locale.
   *
   * @param dateString - The date string to be formatted.
   * @returns A formatted date string in `EEE, MMM DD, YYYY` format.
   * @example
   * ```ts
   * import { DateUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
   * DateUtil.toWeekDayMonthDayYear('2024-11-12T14:27:00.000Z'); // Returns "Sun, Nov 12, 2024"
   * ```
   */
  toWeekDayMonthDayYear: (dateString: string) => string;

  /**
   * Converts a date string to the format `EEE, MMM DD` based on the user's locale.
   *
   * @param dateString - The date string to be formatted.
   * @returns A formatted date string in `EEE, MMM DD` format.
   * @example
   * ```ts
   * import { DateUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
   * DateUtil.toWeekDayMonthDay('2024-11-12T14:27:00.000Z'); // Returns "Sun, Nov 12"
   * ```
   */
  toWeekDayMonthDay: (dateString: string) => string;

  /**
   * Converts a date string to the format `MMM YYYY` based on the user's locale.
   *
   * @param dateString - The date string to be formatted.
   * @returns A formatted date string in `MMM YYYY` format.
   * @example
   * ```ts
   * DateUtil.toMonthYear('2021-10-22');
   * ```
   */
  toMonthYear: (dateString: string) => string;

  /**
   * Checks if a given date string represents today's date or a future date.
   *
   * @param dateStr - The date string to be checked.
   * @returns `true` if the date is today or in the future, `false` otherwise.
   * @example
   * ```ts
   * DateUtil.isTodayOrFutureDate('2025-03-11'); // Returns true if today's date is 2025-03-11 or earlier
   * ```
   */
  isTodayOrFutureDate: (dateStr: string) => boolean;
};
